<template>
  <div class="min-h-screen bg-gray-50 px-4 py-6 pb-28" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header Card -->
      <div class="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-6 text-white">
        <div class="flex items-center gap-3">
          <div class="w-14 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="w-full">
            <h2 class="text-xl font-bold">ثبت پرداخت</h2>
            <p class="text-sm text-indigo-50">ثبت و مدیریت پرداخت‌های فاکتور</p>
          </div>
        </div>
      </div>

      <!-- Price Summary Card -->
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="grid grid-cols-2 gap-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-xs text-gray-600 mb-1">{{ $t('factorPriceQ') }}</p>
            <p class="text-2xl font-bold text-blue-600">{{ factor_price }} <span class="text-sm font-normal">تومان</span></p>
          </div>
          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <p class="text-xs text-gray-600 mb-1">{{ $t('totalPayQ') }}</p>
            <p class="text-2xl font-bold text-green-600">{{ calculate_price }} <span class="text-sm font-normal">تومان</span></p>
          </div>
        </div>
      </div>

      <!-- Add Payment Section -->
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900">افزودن پرداخت</h3>
          <div class="relative">
            <button
              @click="add_payment_menu_open = !add_payment_menu_open"
              class="text-sm whitespace-nowrap bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white py-2 px-4 rounded-md font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              {{ $t('addPay') }}
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </button>
            
            <!-- Payment Type Dropdown -->
            <div v-if="add_payment_menu_open" class="absolute left-0 w-48 mt-2 bg-white border border-gray-300 rounded-lg z-50 max-h-60 overflow-y-auto">
              <div 
                v-for="(item, i) in pay_types" 
                :key="i"
                @click="add_payment(item)"
                class="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
              >
                {{ item.PaymentTypeTitle }}
              </div>
            </div>
          </div>
        </div>

        <!-- Prices List -->
        <div v-if="prices.length > 0" class="mt-6 space-y-2">
          <div 
            v-for="(item, i) in prices"
            :key="i"
            class="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg"
          >
            <div class="flex items-center gap-3 flex-1">
              <div class="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center ">
                <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="w-full">
                <p class="text-sm font-medium text-gray-900">{{ item.payment_name }}</p>
                <p class="text-xs text-gray-500">{{ item.price }} {{ $t('currencyName') }}</p>
              </div>
            </div>
            <button 
              @click="delete_price(i)"
              class="w-8 h-8 max-w-8 p-2 rounded-lg border border-red-600 bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
            >
              <svg class="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Add File Section -->
      <div class="bg-white rounded-xl border border-gray-200 p-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900">{{ $t('addFile') }}</h3>
          <div class="relative">
            <button
              @click="add_file_menu_open = !add_file_menu_open"
              class="text-sm whitespace-nowrap bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white py-2 px-4 rounded-md font-medium transition-all duration-200 flex items-center justify-center gap-2"
            >
              {{ $t('addFile') }}
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </button>
            
            <!-- File Type Dropdown -->
            <div v-if="add_file_menu_open" class="absolute left-0 w-48 mt-2 bg-white border border-gray-300 rounded-lg z-50 max-h-60 overflow-y-auto">
              <div 
                v-for="(item, i) in items" 
                :key="i"
                @click="select_file(i)"
                class="px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm"
              >
                {{ item.title }}
              </div>
            </div>
          </div>
        </div>

        <!-- Files List -->
        <div v-if="files.length > 0" class="mt-6 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
          <div 
            v-for="(item, i) in files"
            :key="i"
            class="relative group"
          >
            <div class="aspect-square rounded-lg overflow-hidden border border-gray-200">
              <img :src="item.preview" class="w-full h-full object-cover" />
            </div>
            <button 
              @click="delete_file(i)"
              class="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Submit Button -->
      <button 
        @click="save()"
        class="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2"
      >
        {{ $t('submitPays') }}
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
        </svg>
      </button>

    </div>

    <!-- Hidden File Input -->
    <input id="fileUpload" type="file" hidden v-on:change="input_file_changed">

    <!-- Camera Dialog -->
    <div v-if="dialog_camera" class="fixed inset-0 bg-black bg-opacity-90 z-999 flex flex-col">
      <!-- Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white flex items-center justify-between">
        <h2 class="text-lg font-bold">{{ $t('camera') }}</h2>
        <button 
          @click="dialog_camera = false"
          class="w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 flex items-center justify-center transition-colors"
        >
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <!-- Camera Preview -->
      <div v-if="isCameraOpen" class="flex-1 flex items-center justify-center bg-black">
        <video v-show="!isPhotoTaken" ref="camera" class="max-w-full max-h-80vh object-contain" autoplay></video>
        <canvas v-show="isPhotoTaken" id="photoTaken" ref="canvas" class="max-w-full max-h-80vh object-contain"></canvas>
      </div>
      
      <!-- Camera Controls -->
      <div v-if="isCameraOpen" class="px-6 py-4 bg-black flex items-center justify-center">
        <button 
          @click="takePhoto"
          class="w-16 h-16 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center transition-colors"
        >
          <svg class="w-8 h-8 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>
      </div>
      
      <!-- Save Button -->
      <div v-if="isPhotoTaken && isCameraOpen" class="px-6 py-4 bg-black flex items-center justify-center">
        <button 
          @click="downloadImage()"
          class="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-8 py-3 rounded-xl font-bold transition-all duration-200"
        >
          {{ $t('save') }}
        </button>
      </div>
    </div>

    <!-- Price Input Dialog -->
    <div v-if="dialog_price" class="fixed inset-0 bg-black bg-opacity-50 z-999 flex items-center justify-center p-4">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md max-h-90vh overflow-hidden flex flex-col">
        <!-- Header -->
        <div class="px-6 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700">
          <h2 class="text-lg font-bold text-white">{{ $t('receivedPrice') }}</h2>
        </div>
        
        <!-- Body -->
        <div class="px-6 py-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('insertAmount') }}</label>
          <input
            v-model="payment_price"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            style="border: 1px solid #d1d5db !important;"
            placeholder="مثلاً 1,000,000"
          />
        </div>
        
        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button 
            class="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
            @click="dialog_price = false"
          >
            {{ $t('cancel') }}
          </button>
          <button 
            class="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-medium rounded-lg transition-all duration-200"
            @click="add_payment_to_list()"
          >
            {{ $t('ok') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import { bus } from '../../main'

  export default {
    name: 'Attachments',
    data () {
      return {
        files: [],
        prices: [],
        items: [
          // {
          //   title: 'دوربین',
          // },
          {
            title: 'گالری',
          },

        ],
        pay_types: [
          // {
          //   title: 'دوربین',
          // },
          {
            title: 'گالری',
          },

        ],
        dialog_camera: false,
        isCameraOpen: false,
        isPhotoTaken: false,
        payment_price: '',
        payment_name: '',
        payment_id: '',
        dialog_price: false,
        factor_price: '',
        add_payment_menu_open: false,
        add_file_menu_open: false,
      }
    },
    watch: {
      payment_price() {
        const formatted = this.formatAmountInput(this.payment_price)
        if (this.payment_price !== formatted) {
          this.payment_price = formatted
        }
      },
    },
    computed: {
      calculate_price () {
        var calculate_prices = 0
        for (var i = 0; i < this.prices.length; i++) {
          calculate_prices += parseInt(this.parseAmountInput(this.prices[i].price), 10) || 0
        }
        return calculate_prices
      }
    },
    mounted () {
      this.items= [
        // {
        //   title: 'دوربین',
        // },
        {
          title: this.$t('gallery'),
        },

      ];
       this.pay_types= [
        // {
        //   title: 'دوربین',
        // },
        {
          title: this.$t('gallery'),
        },

      ];
      this.get_payment_types()
      this.get_payment_info()
      
      // Close menus when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.relative')) {
          this.add_payment_menu_open = false
          this.add_file_menu_open = false
        }
      })
    },
    methods: {

      add_payment (item) {
        this.payment_id = item.PaymentTypeId
        this.payment_name = item.PaymentTypeTitle
        this.dialog_price = true
        this.add_payment_menu_open = false
      },
      add_payment_to_list () {
        if (this.parseAmountInput(this.payment_price) !== '') {
          var obj = { price: this.payment_price, type_id: this.payment_id, payment_name: this.payment_name }
          this.prices.push(obj)
          this.payment_price = ''
          this.dialog_price = false
        }
      },
      select_file (index) {
        if (index == 0) {
          document.getElementById('fileUpload').click()
          this.add_file_menu_open = false
          // this.open_camera();
          // this.dialog_camera = true;
        }
        if (index == 1) {

        }
      },
       save () {
        if (this.calculate_price < this.factor_price) {
          bus.$emit('show_toast', [1500, this.$t('payedAmountLowThanFactor')])
        } else {
          if (this.files.length) {
            for (var i = 0; i < this.files.length; i++) {
              this.upload_image(i)
            }
          } else {
            for (var x = 0; x < this.prices.length; x++) {
              this.send_prices_to_server(x)
            }
          }
        }
      },
      open_camera () {
        this.isCameraOpen = true
        this.createCameraElement()
      },
      delete_file (index) {
        this.files.splice(index, 1)
      },
      delete_price (index) {
        this.prices.splice(index, 1)
      },
      takePhoto () {
        this.isPhotoTaken = true

        const context = this.$refs.canvas.getContext('2d')
        context.drawImage(this.$refs.camera, 0, 0, 450, 337.5)
      },
      downloadImage () {
        if (this.isPhotoTaken == true) {
          this.isCameraOpen = false
          // const download = document.getElementById("downloadPhoto");
          const preview = document.getElementById('photoTaken')
          const canvas = document.getElementById('photoTaken').toDataURL('image/jpeg')
          // .replace("image/jpeg", "image/octet-stream");
          var obj = { file: canvas, preview: preview, upload: 0 }
          this.files.push(obj)
          this.dialog_camera = false
          this.isPhotoTaken = false
        }
        // download.setAttribute("href", canvas);
      },
      input_file_changed () {
        var self = this

        var reader = new FileReader()
        var f = document.getElementById('fileUpload').files

        reader.onloadend = function () {
          // console.log(reader.result);
          var obj = { file: reader.result, preview: reader.result, upload: 0 }
          self.files.push(obj)
        }
        reader.readAsDataURL(f[0])
      },
      createCameraElement () {
        try {
          const constraints = (window.constraints = {
            audio: false,
            video: true
          })

          navigator.mediaDevices
            .getUserMedia(constraints)
            .then(stream => {
              this.$refs.camera.srcObject = stream
            })
            .catch(error => {
              alert(this.$t('deviceHasNoCamera'))
            })
        } catch (err) {
          alert(err)
        }
      },
      upload_image (index) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        var form_data = new FormData()
        form_data.append('UserId', user_id)
        form_data.append('ProcessId', this.$route.params.rec_id)
        form_data.append('Format', 'png')
        form_data.append('ReqType', 8)
        form_data.append('DocType', 1)
        var file = this.dataURLtoFile(this.files[index].file, 'attach.png')
        form_data.append('Picture', file)

        this.axios.post('UploadFile', form_data, {})
          .then(response => {
            // self.send_signature(response.data.ArchiveId,response.data.ImageName);
            if (response.data.error == 0) {
              self.files[index].upload = 1
              self.files[index].ArchiveId = response.data.ArchiveId
              self.files[index].ImageName = response.data.ImageName
              var finish_all = true
              self.files.forEach(function (number) {
                if (number.upload != 1) {
                  finish_all = false
                }
              })
              if (finish_all) {
                self.files[0].upload = 1
                for (var i = 0; i < this.prices.length; i++) {
                  this.send_prices_to_server(i)
                }
              }
            } else {
              self.files[index].upload = 2
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },

      get_payment_types () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,

        }
        this.axios.post('GetPeymentTypeList', send_data, {})
          .then(response => {
            // self.send_signature(response.data.ArchiveId,response.data.ImageName);
            if (response.data.error == 0) {
              self.pay_types = response.data.data
            } else {

            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_payment_info () {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          RequestId: this.$route.params.rec_id,

        }
        this.axios.post('GetPreFactorPeymentInfo', send_data, {})
          .then(response => {
            // self.send_signature(response.data.ArchiveId,response.data.ImageName);
            if (response.data.error == 0) {
              self.factor_price = Math.abs(response.data.TotalPrice)
              if (response.data.TotalPrice == 0) {
                bus.$emit('show_toast', [3000, this.$t('factorHasNoPrice')])

                self.$router.go(-1)
              }
            } else {

            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      dataURLtoFile (dataurl, filename) {
        var arr = dataurl.split(',')
          var mime = arr[0].match(/:(.*?);/)[1]
          var bstr = atob(arr[1])
          var n = bstr.length
          var u8arr = new Uint8Array(n)

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n)
        }

        return new File([u8arr], filename, { type: mime })
      },

      send_prices_to_server (index) {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        // console.log();

        var attachment_array = []
        if (index == 0) {
          self.files.forEach(function (number) {
            var pre_obj = {
              ArchiveId: number.ArchiveId,
              ImageName: number.ImageName,
              Summary: '',
            }
            attachment_array.push(pre_obj)
          })
        }

        const send_data = {
          Attachments: attachment_array,
          UserId: user_id,
          Summary: '',
          PreFactorId: this.$route.params.rec_id,
          Price: this.parseAmountInput(this.prices[index].price),
          PeymentTypeId: this.prices[index].type_id,
        }

        // this.axios.post('/Register' , send_data,{
        this.axios.post('/AddPreFactorPeyment', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              self.$router.go(-1)
            } else {
              bus.$emit('show_toast', [1500, this.$t('errorSendPays')])
            }
          }).catch(e => {
          this.errors.push(e)
        })
      }

    }
  }
</script>
